import { PRODUCT_TYPES } from "@/lib/constants";
import { PRODUCT_KEYS } from "@/lib/entitlements/config";

import type { ProductKey } from "@/lib/entitlements/config";

const devMode = process.env.NODE_ENV === "development";

export const SubscriptionStatus = {
  trialing: "trialing",
  active: "active",
  past_due: "past_due",
  incomplete: "incomplete",
  incomplete_expired: "incomplete_expired",
  paused: "paused",
  unpaid: "unpaid",
  canceled: "canceled",
} as const;

export type SubscriptionStatus = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

// not used in either array below: incomplete

// means we consider the subscription active, even if it's past due (this might change)
export const ACTIVE_SUBSCRIPTION_STATUSES = [
  SubscriptionStatus.active,
  SubscriptionStatus.trialing,
  SubscriptionStatus.past_due,
];

// means we need a new subscription, can not use the existing one
export const INACTIVE_SUBSCRIPTION_STATUSES = [
  SubscriptionStatus.canceled,
  SubscriptionStatus.incomplete_expired,
  SubscriptionStatus.paused,
  SubscriptionStatus.unpaid,
];

// row is in a final state and will never transition again.
// includes "expired" which is our internal status (not a stripe status).
export const TERMINAL_SUBSCRIPTION_STATUSES = ["expired", SubscriptionStatus.canceled] as const;

export const SUBSCRIPTION_TIERS = !devMode
  ? {
      BASIC: "prod_QFZLc7q4mf7Pxa",
      EXTERNAL_BASIC: "EXTERNAL_BASIC",
      PREMIUM: "prod_QFZMmK3Vmblv8j",
      EXTERNAL_PREMIUM: "EXTERNAL_PREMIUM",
    }
  : {
      BASIC: "prod_QGKSFUZZwTa2Gs",
      PREMIUM: "prod_QGKTix07Qb4Jno",
      EXTERNAL_PREMIUM: "EXTERNAL_PREMIUM",
      EXTERNAL_BASIC: "EXTERNAL_BASIC",
    };

// Associates a subscription tier with the products that are available to it
// Whenever a new product is added, it should be added to the corresponding subscription tier
// Let's say we only want to sell SMM, we create a SMM SUBSCRIPTION_TIERS and add that product here,
export const SUBSCRIPTION_PRODUCTS: Record<string, Array<keyof typeof PRODUCT_TYPES>> = {
  [SUBSCRIPTION_TIERS.BASIC]: [
    PRODUCT_TYPES.WEBSITE,
    PRODUCT_TYPES.LOCAL_GEOTAGGING,
    PRODUCT_TYPES.QUOTES,
    PRODUCT_TYPES.REVIEWS,
    PRODUCT_TYPES.CRM,
  ],
  [SUBSCRIPTION_TIERS.EXTERNAL_BASIC]: [
    PRODUCT_TYPES.WEBSITE,
    PRODUCT_TYPES.LOCAL_GEOTAGGING,
    PRODUCT_TYPES.QUOTES,
    PRODUCT_TYPES.REVIEWS,
    PRODUCT_TYPES.CRM,
  ],
  [SUBSCRIPTION_TIERS.PREMIUM]: [
    PRODUCT_TYPES.WEBSITE,
    PRODUCT_TYPES.LOCAL_GEOTAGGING,
    PRODUCT_TYPES.SOCIAL_MEDIA,
    PRODUCT_TYPES.GBP,
    PRODUCT_TYPES.QUOTES,
    PRODUCT_TYPES.REVIEWS,
    PRODUCT_TYPES.CRM,
  ],
  [SUBSCRIPTION_TIERS.EXTERNAL_PREMIUM]: [
    PRODUCT_TYPES.WEBSITE,
    PRODUCT_TYPES.LOCAL_GEOTAGGING,
    PRODUCT_TYPES.SOCIAL_MEDIA,
    PRODUCT_TYPES.GBP,
    PRODUCT_TYPES.QUOTES,
    PRODUCT_TYPES.REVIEWS,
    PRODUCT_TYPES.CRM,
  ],
};

export const PRICING_PLANS = {
  basic: {
    name: "Basic",
    price: 149, // monthly
    features: [
      "No upfront cost",
      "Unlimited Pages",
      "SEO optimized website",
      "Custom Domain & Hosting",
      "Mobile-ready",
    ],
  },
  pro: {
    name: "Premium",
    price: 299, // monthly
    features: [
      "Everything in Basic, plus...",
      "Dedicated project manager",
      "24 hour turnaround for requests",
      "White-glove, hands off experience",
      "Social media posting",
      "Google business profile optimizer",
      "AI call tracking",
    ],
  },
};

// ---------------------------------------------------------------------------
// Subscription plans & addons — single source of truth
// ---------------------------------------------------------------------------

export type BillingPeriod = "monthly" | "yearly";

export interface SubscriptionPlan {
  id: ProductKey;
  pricing: { monthly: number; yearly: number };
  features: string[];
  ctaLink: string;
  popular?: boolean;
  home: { eyebrow: string; name: string; description: string; cta: string };
  pricingPage: { eyebrow: string; name: string; description: string; cta: string };
}

export interface SubscriptionAddon {
  id: ProductKey;
  name: string;
  eyebrow: string;
  description: string;
  pricing: { monthly: number; yearly: number };
  cta: string;
  ctaLink: string;
}

const CAL_LINK = "https://cal.com/team/builtright/launch";

export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: PRODUCT_KEYS.MARKETING_KIT,
    pricing: { monthly: 299, yearly: 250 },
    features: [
      "Website built for your business",
      "Google Business Profile optimization",
      "Social media management",
      "Automatic review requests",
      "Local Geotagging & Photo Showcase",
      "Performance reports",
    ],
    ctaLink: CAL_LINK,
    home: {
      eyebrow: "Get Found Online",
      name: "Marketing Kit",
      description: "Website, social media, and review management, continuously optimized by AI.",
      cta: "Get Marketing Kit",
    },
    pricingPage: {
      eyebrow: "Get Found Online",
      name: "Marketing Kit",
      description: "Everything you need to get found online. Website, SEO, reviews, and social media.",
      cta: "Get Marketing Kit",
    },
  },
  {
    id: PRODUCT_KEYS.MK_CRM_BUNDLE,
    pricing: { monthly: 349, yearly: 290 },
    features: [
      "Everything in Marketing Kit",
      "Everything in CRM",
      "Early access to new features",
      "Priority onboarding",
      "Dedicated support",
    ],
    ctaLink: CAL_LINK,
    popular: true,
    home: {
      eyebrow: "Best Value",
      name: "Full Operations Suite",
      description: "Turn completed jobs into your best salesperson. Marketing & CRM working together.",
      cta: "Get Full Platform",
    },
    pricingPage: {
      eyebrow: "Most Popular",
      name: "Full Platform",
      description: "Marketing, CRM, and payments in one platform. Built for home service businesses.",
      cta: "Get Full Platform",
    },
  },
  {
    id: PRODUCT_KEYS.CRM,
    pricing: { monthly: 99, yearly: 82 },
    features: [
      "Lead pipeline",
      "Job scheduling and dispatch",
      "Quotes and invoices",
      "Automated follow-ups",
      "Team dashboards",
      "Mobile app for your crew",
    ],
    ctaLink: CAL_LINK,
    home: {
      eyebrow: "Run Your Operations",
      name: "CRM",
      description:
        "Capture leads, manage communications, send quotes, close jobs, invoice clients, and collect reviews.",
      cta: "Get CRM",
    },
    pricingPage: {
      eyebrow: "Run Your Operations",
      name: "CRM",
      description: "Your command center for leads, jobs, scheduling, and payments.",
      cta: "Get CRM",
    },
  },
];

export const VOICE_AGENT_ADDON: SubscriptionAddon = {
  id: PRODUCT_KEYS.VOICE_ASSISTANT,
  name: "Voice Agent",
  eyebrow: "Never Miss a Call",
  description: "Answers every call, scores the lead, books the job, and logs the details automatically.",
  pricing: { monthly: 99, yearly: 82 },
  cta: "Add Voice Agent",
  ctaLink: CAL_LINK,
};

export const TRIAL_PERIOD_DAYS = process.env.NODE_ENV === "production" ? 7 : 1;

export const YEARLY_DISCOUNT_PERCENT = 25;
